import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  withStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import { appService } from '../../App/app.service';
import PipelineBuildBarChart from './PipelineBuildBarChart';
import CumulativePipelineBuildLineChart from './CumulativePipelineBuildLineChart';
import NewCustomerDealsWonStackedBar from './NewCustomerDealsWonStackedBar';
import NewCustomerDealsWonTable from './NewCustomerDealsWonTable';
import GlobalDownloadButton from '../../components/GlobalDownloadButton';
import AddHeaderDescriptionWithHelperText from '../../components/HelperTextDescription';
import QualifiedPipelineCoverageChart from './QualifiedPipelineCoverageChart';
import RegionalSalesReviewPackagePPTX from '../RegionalSalesReviewPackage/RegionalSalesReviewPackagePPTX';
import SingleLevelDrilldown from '../../components/Drilldown/SingleLevelDrilldown';
import DrilldownWithSLD from '../../components/Drilldown/DrilldownWithSLD';
import ExpectedAcvPage from '../NewWaterFall/ExpectedAcvPage';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1.2rem',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    '& .MuiCardContent-root': {
      padding: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2),
      },
    },
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 280,
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      minHeight: 300,
      padding: theme.spacing(2),
    },
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  filterWrapper: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  filterContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'inline-flex',
    width: 'auto',
  },
  formControl: {
    minWidth: 200,
  },
  teamFilterFormControl: {
    minWidth: 200,
  },
  goButton: {
    borderRadius: 4,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
  },
  filterLabel: {
    color: '#000',
  },
  filterLabelActive: {
    color: '#4472c4',
  },
});

const PipelineReviewPackage = (props) => {
  const { classes, pipelineBuildData, cumulativePipelineBuildData, cumulativePipelineByVerticalData, newCustomerDealsWonData, qualifiedPipelineCoverageData, prpFirstLevelTeams, prpDefaultTeam,helperTextCharts } = props;

  // Local state for pending team selection (before clicking Go)
  const [pendingTeam, setPendingTeam] = useState('All');
  // Applied team filter (after clicking Go)
  const [appliedTeam, setAppliedTeam] = useState('All');
  // Track if initial data has been loaded
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Drilldown state
  const [secondLevelLocation, setSecondLevelLocation] = useState('');
  // Qualified Pipeline Coverage drilldown state
  const [qualifiedPipelineDrilldownOpen, setQualifiedPipelineDrilldownOpen] = useState(false);
  const [qualifiedPipelineDrilldownFilters, setQualifiedPipelineDrilldownFilters] = useState([]);

  // Reset to 'All' when component mounts
  useEffect(() => {
    setPendingTeam('All');
    setAppliedTeam('All');
    setInitialLoadDone(false);
  }, []);

  // Fetch first-level teams on mount
  useEffect(() => {
    props.getFirstLevelTeams();
  }, []);

  // Set default team when teams are loaded (uses manager's team if applicable)
  useEffect(() => {
    if (!initialLoadDone && prpFirstLevelTeams && prpFirstLevelTeams.length > 0) {
      const defaultTeam = prpDefaultTeam || 'All';
      setPendingTeam(defaultTeam);
      setAppliedTeam(defaultTeam);
      const filters = defaultTeam !== 'All' ? [{ name: 'team', value: defaultTeam }] : [];
      props.getPipelineBuildData(filters);
      props.getCumulativePipelineBuildData(filters);
      props.getCumulativePipelineByVerticalData(filters);
      props.getNewCustomerDealsWonData(filters);
      props.getQualifiedPipelineCoverageData(filters);
      setInitialLoadDone(true);
    }
  }, [prpFirstLevelTeams, prpDefaultTeam, initialLoadDone]);

  // Handle team filter change (just updates pending, doesn't fetch)
  const handleTeamChange = (event) => {
    setPendingTeam(event.target.value);
  };

  // Handle Go button click - apply the filter and fetch data
  const handleGo = () => {
    setAppliedTeam(pendingTeam);
    const filters = pendingTeam !== 'All' ? [{ name: 'team', value: pendingTeam }] : [];
    props.getPipelineBuildData(filters);
    props.getCumulativePipelineBuildData(filters);
    props.getCumulativePipelineByVerticalData(filters);
    props.getNewCustomerDealsWonData(filters);
    props.getQualifiedPipelineCoverageData(filters);
  };

  // Handler for second level drilldown (opportunity details)
  const handleSecondLevelDrilldown = (location, oppId) => {
    setSecondLevelLocation(location);
    if (location === 'drilldownOppId') {
      props.getExpectedAcvData(oppId);
    }
  };

  // Handler for qualified pipeline coverage drilldown
  const handleQualifiedPipelineDrilldown = ({ quarter }) => {
    const filters = [
      { name: 'quarter', value: quarter },
    ];
    // Include team filter if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }

    setQualifiedPipelineDrilldownOpen(true);

    // Build display filters
    const displayFilters = [
      { title: 'Quarter', value: quarter },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    setQualifiedPipelineDrilldownFilters(displayFilters);

    props.getQualifiedPipelineCoverageDrilldown(filters);
  };

  // Handler to close qualified pipeline coverage drilldown
  const handleQualifiedPipelineDrilldownBack = () => {
    setQualifiedPipelineDrilldownOpen(false);
    setQualifiedPipelineDrilldownFilters([]);
    setSecondLevelLocation('');
    props.clearQualifiedPipelineCoverageDrilldown();
    props.clearExpectedAcvData();
  };

  // Step back from second level to first level drilldown
  const handleQualifiedPipelineStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  const isPipelineBuildLoading = !pipelineBuildData || pipelineBuildData === "" || !pipelineBuildData.charts;
  const isCumulativePipelineBuildLoading = !cumulativePipelineBuildData || cumulativePipelineBuildData === "" || !cumulativePipelineBuildData.charts;
  const isCumulativePipelineByVerticalLoading = !cumulativePipelineByVerticalData || cumulativePipelineByVerticalData === "" || !cumulativePipelineByVerticalData.charts;
  // Only show loading when data hasn't been fetched yet, not when data is empty
  const isNewCustomerDealsWonLoading = !newCustomerDealsWonData || newCustomerDealsWonData === "";
  const isQualifiedPipelineCoverageLoading = !qualifiedPipelineCoverageData || qualifiedPipelineCoverageData === "";

  return (
    <div className={classes.root}>
      <AddHeaderDescriptionWithHelperText
        header={`Pipeline Review Package`}
        description={`Pipeline Review Package.`}
        helperTextCharts={helperTextCharts}
      />
      {/* Team Filter */}
      <Box className={classes.filterWrapper}>
        <Paper className={classes.filterContainer} elevation={0}>
          <FormControl className={classes.formControl}>
            <InputLabel
              className={classes.filterLabel}
              id="team-filter-label"
              style={{
                color: pendingTeam !== '' && pendingTeam !== 'All' ? '#4472c4' : '#000',
              }}
            >
              Team:
            </InputLabel>
            <Select
              labelId="team-filter-label"
              id="team-filter"
              value={pendingTeam}
              onChange={handleTeamChange}
              label="Team:"
            >
              <MenuItem value="All">
                All
              </MenuItem>
              {prpFirstLevelTeams && prpFirstLevelTeams.map((team) => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
        <Button
          color="primary"
          variant="contained"
          onClick={handleGo}
          className={classes.goButton}
        >
          GO
        </Button>
      </Box>

        {/* Qualified Pipeline Coverage (Paper 5) */}
      <Paper className={classes.paper} style={{ marginTop: 32 }}>
        <div className={classes.header}>
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {qualifiedPipelineCoverageData?.title || 'Qualified Pipeline Coverage'}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>
        </div>

        {isQualifiedPipelineCoverageLoading ? (
          <Skeleton variant="rect" animation="wave" height={350} width="100%" />
        ) : qualifiedPipelineCoverageData?.chartData?.length > 0 ? (
          <QualifiedPipelineCoverageChart
            data={qualifiedPipelineCoverageData.chartData}
            title={qualifiedPipelineCoverageData.title}
            onDrill={handleQualifiedPipelineDrilldown}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Pipeline Build Charts */}
      <Paper className={classes.paper}>
        <div className={classes.header}>
          {/* <GlobalDownloadButton
            dashboardTitle={`Pipeline Review Package`}
            fileName={`Pipeline_Review_Package`}
          /> */}
          <RegionalSalesReviewPackagePPTX
            dashboardTitle="Pipeline Review Package"
            fileName="Pipeline_Review_Package"
          />
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {pipelineBuildData?.title || 'Qualified Pipeline build ($M) by month, by source'}
            </Typography>
            <span className={classes.updateFrequency}>
              Live Data
            </span>
          </Box>
        </div>

        {/* Bar Charts - 3x2 Grid */}
        {isPipelineBuildLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5].map((index) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={350} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : pipelineBuildData?.charts && pipelineBuildData.charts.length > 0 ? (
          <Grid container spacing={2}>
            {pipelineBuildData.charts.map((chart) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={chart.source}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <PipelineBuildBarChart
                        data={chart.data}
                        title={chart.title}
                        targetLine={chart.targetLine}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Cumulative Pipeline Build Charts */}
      <Paper className={classes.paper} style={{ marginTop: 32 }}>
        <div className={classes.header}>
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {cumulativePipelineBuildData?.title || 'Cumulative Pipeline build ($M) by source'}
            </Typography>
            <span className={classes.updateFrequency}>
              Live Data
            </span>
          </Box>
        </div>

        {/* Line Charts - 3x2 Grid */}
        {isCumulativePipelineBuildLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5].map((index) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={350} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : cumulativePipelineBuildData?.charts && cumulativePipelineBuildData.charts.length > 0 ? (
          <Grid container spacing={2}>
            {cumulativePipelineBuildData.charts.map((chart) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={chart.source}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <CumulativePipelineBuildLineChart
                        data={chart.data}
                        title={chart.title}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Cumulative Pipeline Build Charts - Monthly View */}
      {/* <Paper className={classes.paper} style={{ marginTop: 32 }}>
        <div className={classes.header}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {(cumulativePipelineBuildData?.title || "Cumulative Pipeline build ($M) by source")}
            </Typography>
            <span className={classes.updateFrequency}>{"Live Data"}</span>
          </Box>
        </div>
        {isCumulativePipelineBuildLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5].map((index) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={`cm-skel-${index}`}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={350} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : cumulativePipelineBuildData?.charts && cumulativePipelineBuildData.charts.length > 0 ? (
          <Grid container spacing={2}>
            {cumulativePipelineBuildData.charts.map((chart) => {
              const dataArr = Array.isArray(chart.data) ? chart.data : [];
              const monthsMeta = cumulativePipelineBuildData?.months || 
                dataArr.map(d => ({ yearMonth: d.yearMonth, label: d.month || d.label }))
                .filter(m => m.yearMonth && m.label);

              const now = new Date();
              const yyyy = now.getFullYear();
              const mm = String(now.getMonth() + 1).padStart(2, "0");
              const nowYM = `${yyyy}-${mm}`;

              let currentIdx = monthsMeta.findIndex((m) => m.yearMonth === nowYM);
              
              // Fallback: If current month isn't in meta, find last month with actual data
              if (currentIdx === -1) {
                currentIdx = monthsMeta.findLastIndex((m) =>
                  dataArr.some((d) => d.yearMonth === m.yearMonth && d.actualCum != null)
                );
              }

              // If still not found, default to showing all data or first month
              if (currentIdx === -1) currentIdx = monthsMeta.length - 1;

              // Collect all labels from the start of the array up to currentIdx
              const monthsToShow = monthsMeta
                .slice(0, currentIdx + 1) // Slice from index 0 to current
                .map(m => m.label);

              const cumulativeMonthData = dataArr.filter((d) =>
                monthsToShow.includes(d.month || d.label)
              );

              return (
                <Grid item xs={12} sm={6} md={4} lg={4} key={`${chart.source}-ytd-view`}>
                  <Card className={classes.chartCard}>
                    <CardContent>
                      <div className={classes.chartContainer}>
                        <CumulativePipelineBuildLineChart
                          data={cumulativeMonthData}
                          title={`${chart.title}`}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">No data available</Typography>
          </Box>
        )}
      </Paper> */}

      {/* Cumulative Pipeline Build by Vertical Charts */}
      <Paper className={classes.paper} style={{ marginTop: 32 }}>
        <div className={classes.header}>
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {cumulativePipelineByVerticalData?.title || 'Cumulative Pipeline build ($M) by vertical'}
            </Typography>
            <span className={classes.updateFrequency}>
              Live Data
            </span>
          </Box>
        </div>

        {/* Line Charts - 3x2 Grid */}
        {isCumulativePipelineByVerticalLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6, 7].map((index) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={350} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : cumulativePipelineByVerticalData?.charts && cumulativePipelineByVerticalData.charts.length > 0 ? (
          <Grid container spacing={2}>
            {cumulativePipelineByVerticalData.charts.map((chart) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={chart.industry}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <CumulativePipelineBuildLineChart
                        data={chart.data}
                        title={chart.title}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Cumulative Pipeline Build by Vertical - Monthly View */}
      {/* <Paper className={classes.paper} style={{ marginTop: 32 }}>
        <div className={classes.header}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {(cumulativePipelineByVerticalData?.title || "Cumulative Pipeline build ($M) by vertical")}
            </Typography>
            <span className={classes.updateFrequency}>{"Live Data"}</span>
          </Box>
        </div>
        {isCumulativePipelineByVerticalLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5].map((index) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={`cv-skel-${index}`}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={350} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : cumulativePipelineByVerticalData?.charts && cumulativePipelineByVerticalData.charts.length > 0 ? (
          <Grid container spacing={2}>
            {cumulativePipelineByVerticalData.charts.map((chart) => {
              const dataArr = Array.isArray(chart.data) ? chart.data : [];
              const monthsMeta = cumulativePipelineByVerticalData?.months || 
                dataArr.map(d => ({ yearMonth: d.yearMonth, label: d.month || d.label }))
                .filter(m => m.yearMonth && m.label);

              const now = new Date();
              const yyyy = now.getFullYear();
              const mm = String(now.getMonth() + 1).padStart(2, "0");
              const nowYM = `${yyyy}-${mm}`;

              let currentIdx = monthsMeta.findIndex((m) => m.yearMonth === nowYM);
              
              if (currentIdx === -1) {
                currentIdx = monthsMeta.findLastIndex((m) =>
                  dataArr.some((d) => d.yearMonth === m.yearMonth && d.actualCum != null)
                );
              }

              if (currentIdx === -1) currentIdx = monthsMeta.length - 1;

              const monthsToShow = monthsMeta
                .slice(0, currentIdx + 1)
                .map(m => m.label);

              const cumulativeVerticalData = dataArr.filter((d) =>
                monthsToShow.includes(d.month || d.label)
              );

              return (
                <Grid item xs={12} sm={6} md={4} lg={4} key={`${chart.industry}-ytd-view`}>
                  <Card className={classes.chartCard}>
                    <CardContent>
                      <div className={classes.chartContainer}>
                        <CumulativePipelineBuildLineChart
                          data={cumulativeVerticalData}
                          title={`${chart.title}`}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">No data available</Typography>
          </Box>
        )}
      </Paper> */}

      {/* New Customer Deals Won Charts (Paper 4) */}
      <Paper className={classes.paper} style={{ marginTop: 32 }}>
        <div className={classes.header}>
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {newCustomerDealsWonData?.title}
            </Typography>
            <span className={classes.updateFrequency}>
              Live Data
            </span>
          </Box>
        </div>

        {isNewCustomerDealsWonLoading ? (
          <Grid container spacing={3}>
            {[1, 2].map((index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={400} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Skeleton variant="rect" animation="wave" height={200} width="100%" />
            </Grid>
          </Grid>
        ) : newCustomerDealsWonData?.dealsCountChart && newCustomerDealsWonData?.acvChart ? (
          <>
            <Grid container spacing={3}>
              {/* Stacked Bar Chart - # of Deals */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <NewCustomerDealsWonStackedBar
                        data={newCustomerDealsWonData.dealsCountChart.data}
                        title={newCustomerDealsWonData.dealsCountChart.title}
                        sources={newCustomerDealsWonData.sources}
                        yAxisLabel="# of Deals"
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Stacked Bar Chart - ACV ($M) */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <NewCustomerDealsWonStackedBar
                        data={newCustomerDealsWonData.acvChart.data}
                        title={newCustomerDealsWonData.acvChart.title}
                        sources={newCustomerDealsWonData.sources}
                        yAxisLabel="ACV ($M)"
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Summary Table */}
            {newCustomerDealsWonData.summaryTable && (
              <Box mt={3}>
                <NewCustomerDealsWonTable summaryTable={newCustomerDealsWonData.summaryTable} />
              </Box>
            )}
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Qualified Pipeline Coverage Drilldown - First Level */}
      {qualifiedPipelineDrilldownOpen && secondLevelLocation !== 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={'Qualified Pipeline Opportunities'}
            back={handleQualifiedPipelineDrilldownBack}
            filters={qualifiedPipelineDrilldownFilters}
          >
            <DrilldownWithSLD
              header={'Open Qualified Opportunities'}
              body={props.qualifiedPipelineCoverageDrilldownData}
              company={props.company}
              Value_Label={props.Value_Label}
              handleSecondLevelDrilldown={handleSecondLevelDrilldown}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

      {/* Qualified Pipeline Coverage Drilldown - Second Level (Opportunity Change Events) */}
      {qualifiedPipelineDrilldownOpen && secondLevelLocation === 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={"Opportunity Change Events"}
            back={handleQualifiedPipelineDrilldownBack}
            stepBack={handleQualifiedPipelineStepBack}
            filters={[]}
          >
            <ExpectedAcvPage
              body={props.ExpectedAcvData}
              Value_Label={props.Value_Label}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, pipelineBuildData, cumulativePipelineBuildData, cumulativePipelineByVerticalData, newCustomerDealsWonData, qualifiedPipelineCoverageData, qualifiedPipelineCoverageDrilldownData, prpFirstLevelTeams, prpDefaultTeam, ExpectedAcvData } = state.app;

  return {
    company: user.company,
    Value_Label: user.Value_Label,
    pipelineBuildData,
    cumulativePipelineBuildData,
    cumulativePipelineByVerticalData,
    newCustomerDealsWonData,
    qualifiedPipelineCoverageData,
    qualifiedPipelineCoverageDrilldownData,
    prpFirstLevelTeams,
    helperTextCharts: user.elements.flatMap((item) => item.charts || []),
    prpDefaultTeam,
    ExpectedAcvData,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getFirstLevelTeams: () => {
    dispatch({ type: "get_prp_first_level_teams_request" });
    appService.getPipelineReviewFirstLevelTeams().then(
      (json) => {
        dispatch({ type: "get_prp_first_level_teams_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_prp_first_level_teams_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_prp_first_level_teams_failure", error });
      }
    );
  },
  getPipelineBuildData: (filters = []) => {
    dispatch({ type: "get_pipeline_build_data_request" });
    appService.getPipelineReviewPipelineBuild(filters).then(
      (json) => {
        dispatch({ type: "get_pipeline_build_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_pipeline_build_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_pipeline_build_data_failure", error });
      }
    );
  },
  getCumulativePipelineBuildData: (filters = []) => {
    dispatch({ type: "get_cumulative_pipeline_build_data_request" });
    appService.getPipelineReviewCumulativePipelineBuild(filters).then(
      (json) => {
        dispatch({ type: "get_cumulative_pipeline_build_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_cumulative_pipeline_build_data_failure",
            error: "Something went wrong",

          });
        else dispatch({ type: "get_cumulative_pipeline_build_data_failure", error });
      }
    );
  },
  getCumulativePipelineByVerticalData: (filters = []) => {
    dispatch({ type: "get_cumulative_pipeline_by_vertical_data_request" });
    appService.getPipelineReviewCumulativePipelineByVertical(filters).then(
      (json) => {
        dispatch({ type: "get_cumulative_pipeline_by_vertical_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_cumulative_pipeline_by_vertical_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_cumulative_pipeline_by_vertical_data_failure", error });
      }
    );
  },
  getNewCustomerDealsWonData: (filters = []) => {
    dispatch({ type: "get_new_customer_deals_won_data_request" });
    appService.getPipelineReviewNewCustomerDealsWon(filters).then(
      (json) => {
        dispatch({ type: "get_new_customer_deals_won_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_new_customer_deals_won_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_new_customer_deals_won_data_failure", error });
      }
    );
  },
  getQualifiedPipelineCoverageData: (filters = []) => {
    dispatch({ type: "get_qualified_pipeline_coverage_data_request" });
    appService.getPipelineReviewQualifiedPipelineCoverage(filters).then(
      (json) => {
        dispatch({ type: "get_qualified_pipeline_coverage_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_qualified_pipeline_coverage_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_qualified_pipeline_coverage_data_failure", error });
      }
    );
  },
  getQualifiedPipelineCoverageDrilldown: (filters = []) => {
    dispatch({ type: "get_qualified_pipeline_coverage_drilldown_request" });
    appService.getPipelineReviewQualifiedPipelineCoverageDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_qualified_pipeline_coverage_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_qualified_pipeline_coverage_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_qualified_pipeline_coverage_drilldown_failure", error });
      }
    );
  },
  clearQualifiedPipelineCoverageDrilldown: () => {
    dispatch({ type: "clear_qualified_pipeline_coverage_drilldown" });
  },
  getExpectedAcvData: (oppId) => {
    dispatch({ type: "get_expectedAcv_drilldown_data_request" });
    appService.getExpectedAcvData(oppId).then(
      (json) => {
        dispatch({ type: "get_expectedAcv_drilldown_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_expectedAcv_drilldown_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_expectedAcv_drilldown_data_failure", error });
      }
    );
  },
  clearExpectedAcvData: () => {
    dispatch({ type: "clear_expected_acv_data" });
  },
});

const connectedPipelineReviewPackage = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(PipelineReviewPackage));

export default connectedPipelineReviewPackage;
